import { useEffect, useState, useRef } from "react";
import { api, apiErr, fileUrl } from "@/admin/api";
import { PageTitle, StatusBadge } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Plus, Star, Loader2, ImagePlus, Eye, MoreVertical, Copy, Archive, RotateCcw, Pencil, Trash2,
  MapPin, CalendarDays, MessageSquare, Clock, GripVertical, ExternalLink, X, ImageOff, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Draft", "Coming Soon", "Open for Booking", "Waitlist", "Sold Out", "Completed", "Archived"];
const PUBLIC_STATUSES = ["Coming Soon", "Open for Booking", "Waitlist", "Sold Out"];
const TYPES = ["Resort Stay", "Cruise", "Group Travel", "Travel Essential", "Seasonal Offer"];
const CTA_LABELS = ["Book Now", "Request Pricing", "Join Waitlist", "Learn More", "Reserve My Spot", "Request Information"];
const CTA_ACTIONS = [
  { v: "inquiry_form", l: "Open inquiry form" },
  { v: "external_link", l: "External link" },
  { v: "contact_page", l: "Contact page" },
];

const EMPTY = {
  title: "", travel_type: "Resort Stay", destinations: [], short_description: "", full_description: "",
  starting_price: "", pricing_note: "", start_date: "", end_date: "", booking_deadline: "", deposit_amount: "",
  cancellation_notes: "", image_url: "", gallery: [], status: "Draft", featured: false,
  cta_label: "Request Information", cta_action: "inquiry_form", cta_url: "",
  publish_at: "", expire_at: "",
  whats_included: [], whats_not_included: [], accommodation: "", itinerary: [], offer_faqs: [],
  show_gallery: true, show_itinerary: false, show_accommodation: false, show_faqs: false,
  show_meet_juliet: true, show_related: true,
};

const fmtDates = (o) => {
  if (!o.start_date && !o.end_date) return null;
  const f = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
  return [f(o.start_date), f(o.end_date)].filter(Boolean).join(" – ");
};
const fmtUpdated = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";

export default function Offers() {
  const [items, setItems] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [destInput, setDestInput] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [preview, setPreview] = useState(false);
  const [featuredCount, setFeaturedCount] = useState(3);
  const [dragId, setDragId] = useState(null);
  const fileRef = useRef();
  const galleryRef = useRef();

  const load = async () => {
    try {
      const params = { include_archived: true };
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/admin/offers", { params });
      setItems(data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => {
    api.get("/site-content").then(({ data }) => setFeaturedCount(data.featured_offers_count ?? 3)).catch(() => {});
  }, []);

  const saveCount = async (n) => {
    const val = Math.max(1, Math.min(12, Number(n) || 1));
    setFeaturedCount(val);
    try { await api.patch("/admin/site-content", { featured_offers_count: val }); toast.success("Homepage featured count updated"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const featuredList = (items || []).filter((o) => o.featured);
  const needsImage = (items || []).filter((o) => PUBLIC_STATUSES.includes(o.status) && !o.image_url);

  const onDropReorder = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const ids = featuredList.map((o) => o.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setDragId(null);
    try { await api.post("/admin/offers/reorder", { ids }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setDestInput(""); setEditingId(null); setPreview(false); setOpen(true); };
  const openEdit = (o) => {
    setForm({ ...EMPTY, ...o, destinations: o.destinations || [] });
    setDestInput((o.destinations || []).join(", "));
    setEditingId(o.id); setPreview(false); setOpen(true);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", data.url);
      toast.success("Image uploaded");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploading(false); }
  };

  const uploadGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingGallery(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ...urls] }));
      toast.success(`${urls.length} image(s) added to gallery`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploadingGallery(false); if (galleryRef.current) galleryRef.current.value = ""; }
  };

  const removeGallery = (url) => setForm((f) => ({ ...f, gallery: (f.gallery || []).filter((g) => g !== url) }));

  // Line-based list helpers for included / not-included
  const linesToList = (text) => text.split("\n").map((s) => s.trim()).filter(Boolean);
  const listToLines = (list) => (list || []).join("\n");

  // Repeater helpers for itinerary + FAQs
  const addRow = (key, row) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), row] }));
  const updateRow = (key, idx, patch) => setForm((f) => ({ ...f, [key]: (f[key] || []).map((r, i) => i === idx ? { ...r, ...patch } : r) }));
  const removeRow = (key, idx) => setForm((f) => ({ ...f, [key]: (f[key] || []).filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Offer title is required."); return; }
    const payload = { ...form, destinations: destInput.split(",").map((s) => s.trim()).filter(Boolean) };
    setSaving(true);
    try {
      if (editingId) await api.patch(`/admin/offers/${editingId}`, payload);
      else await api.post("/admin/offers", payload);
      toast.success(editingId ? "Offer updated" : "Offer created");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const quickStatus = async (o, status) => {
    try { await api.patch(`/admin/offers/${o.id}`, { ...o, status }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleFeatured = async (o) => {
    try { await api.patch(`/admin/offers/${o.id}`, { ...o, featured: !o.featured }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const act = async (path, o, msg) => {
    try { await api.post(`/admin/offers/${o.id}/${path}`); toast.success(msg); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Permanently delete "${o.title}"? Consider archiving instead.`)) return;
    try { await api.delete(`/admin/offers/${o.id}`); toast.success("Offer deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="admin-offers">
      <PageTitle title="Travel Offers" subtitle="Manage your trips like products — status, dates, capacity and demand at a glance">
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="offer-status-filter" className="rounded-full border-ink/20 w-40"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="new-offer" onClick={openNew} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white gap-2">
            <Plus size={16} /> New Offer
          </Button>
        </div>
      </PageTitle>

      {needsImage.length > 0 && (
        <div data-testid="hero-image-nudge" className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5 flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-500 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-amber-900">
              {needsImage.length === 1
                ? "1 live trip is missing a hero image"
                : `${needsImage.length} live trips are missing a hero image`}
            </p>
            <p className="text-xs text-amber-800/80 mt-0.5">Add a hero image so these pages never launch with a plain background. Click a trip to fix it:</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {needsImage.map((o) => (
                <button key={o.id} data-testid={`nudge-offer-${o.id}`} onClick={() => openEdit(o)}
                  className="inline-flex items-center gap-1.5 text-xs bg-white border border-amber-200 text-amber-900 px-2.5 py-1 rounded-full hover:bg-amber-100 transition-colors">
                  <ImageOff size={12} /> {o.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {featuredList.length > 0 && (
        <div data-testid="featured-panel" className="bg-white rounded-2xl border border-bone p-5 mb-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="font-serif text-xl font-semibold text-ink">Featured on Homepage</h2>
              <p className="text-xs text-ink-soft">Drag to reorder. The homepage shows the first <b>{featuredCount}</b>.</p>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-ink-soft whitespace-nowrap"># to show</Label>
              <Input data-testid="featured-count" type="number" min={1} max={12} value={featuredCount}
                onChange={(e) => setFeaturedCount(e.target.value)} onBlur={(e) => saveCount(e.target.value)}
                className="w-20 rounded-xl border-bone" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {featuredList.map((o, idx) => (
              <div key={o.id} draggable data-testid={`featured-chip-${o.id}`}
                onDragStart={() => setDragId(o.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDropReorder(o.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-grab active:cursor-grabbing transition-colors ${dragId === o.id ? "border-terracotta bg-terracotta/5" : "border-bone bg-sand"} ${idx < featuredCount ? "" : "opacity-50"}`}>
                <GripVertical size={14} className="text-ink-soft/50" />
                <span className="text-sm font-medium text-ink">{o.title}</span>
                {idx >= featuredCount && <span className="text-[10px] text-ink-soft">(hidden)</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {!items ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-terracotta" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-bone p-16 text-center">
          <p className="text-ink-soft">No offers here. Create your first travel offer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {items.map((o) => {
            const dates = fmtDates(o);
            return (
              <div key={o.id} data-testid={`offer-card-${o.id}`} className={`bg-white rounded-2xl border overflow-hidden shadow-sm flex ${o.status === "Archived" ? "opacity-60 border-bone" : "border-bone"}`}>
                <div className="w-36 sm:w-44 shrink-0 bg-bone relative">
                  {o.image_url
                    ? <img src={fileUrl(o.image_url)} alt={o.title} className="w-full h-full object-cover" />
                    : <div className="w-full h-full min-h-[180px] flex items-center justify-center text-ink-soft/40"><ImagePlus size={28} /></div>}
                  {o.featured && <span className="absolute top-2 left-2 bg-terracotta text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1"><Star size={10} fill="currentColor" /> Featured</span>}
                  {PUBLIC_STATUSES.includes(o.status) && !o.image_url && (
                    <span data-testid={`offer-needs-image-${o.id}`} className="absolute bottom-2 left-2 right-2 bg-amber-500/95 text-white text-[10px] px-2 py-1 rounded-lg flex items-center gap-1 justify-center">
                      <ImageOff size={11} /> Add a hero image
                    </span>
                  )}
                </div>

                <div className="flex-1 p-4 min-w-0 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-wide text-terracotta font-semibold">{o.travel_type}</p>
                      <h3 className="font-serif text-xl font-semibold text-ink truncate">{o.title}</h3>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button data-testid={`offer-menu-${o.id}`} variant="ghost" size="icon" className="rounded-lg shrink-0 text-ink-soft"><MoreVertical size={18} /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem data-testid={`offer-edit-${o.id}`} onClick={() => openEdit(o)}><Pencil size={14} className="mr-2" /> Edit</DropdownMenuItem>
                        <DropdownMenuItem data-testid={`offer-duplicate-${o.id}`} onClick={() => act("duplicate", o, "Offer duplicated")}><Copy size={14} className="mr-2" /> Duplicate</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { openEdit(o); setTimeout(() => setPreview(true), 50); }}><Eye size={14} className="mr-2" /> Preview</DropdownMenuItem>
                        {o.slug && (
                          <DropdownMenuItem data-testid={`offer-viewpage-${o.id}`} onClick={() => window.open(`/offers/${o.slug}`, "_blank", "noopener")}>
                            <ExternalLink size={14} className="mr-2" /> View public page
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        {o.status === "Archived"
                          ? <DropdownMenuItem data-testid={`offer-restore-${o.id}`} onClick={() => act("restore", o, "Offer restored")}><RotateCcw size={14} className="mr-2" /> Restore</DropdownMenuItem>
                          : <DropdownMenuItem data-testid={`offer-archive-${o.id}`} onClick={() => act("archive", o, "Offer archived")}><Archive size={14} className="mr-2" /> Archive</DropdownMenuItem>}
                        <DropdownMenuItem data-testid={`offer-delete-${o.id}`} onClick={() => remove(o)} className="text-red-600"><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="mt-2 space-y-1 text-sm text-ink-soft flex-1">
                    {dates && <p className="flex items-center gap-1.5"><CalendarDays size={13} /> {dates}</p>}
                    {o.destinations?.length > 0 && <p className="flex items-center gap-1.5 truncate"><MapPin size={13} /> {o.destinations.join(" • ")}</p>}
                    {o.booking_deadline && <p className="flex items-center gap-1.5 text-xs"><Clock size={12} /> Book by {o.booking_deadline}</p>}
                  </div>

                  <div className="flex items-center flex-wrap gap-2 mt-3 pt-3 border-t border-bone">
                    <StatusBadge status={o.status} />
                    {o.is_live && <span className="inline-flex items-center gap-1 text-xs text-green-600"><span className="h-2 w-2 rounded-full bg-green-500" /> Live</span>}
                    {o.scheduled_state && <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"><Clock size={11} /> {o.scheduled_state}</span>}
                    <span className="inline-flex items-center gap-1 text-xs text-ink-soft"><MessageSquare size={12} /> {o.inquiry_count} inquiries</span>
                    <span className="ml-auto text-[11px] text-ink-soft/70">Updated {fmtUpdated(o.updated_at)}</span>
                  </div>

                  <div className="flex items-center gap-3 mt-3">
                    <Select value={o.status} onValueChange={(v) => quickStatus(o, v)}>
                      <SelectTrigger data-testid={`offer-quickstatus-${o.id}`} className="rounded-lg border-bone h-9 text-xs w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    <button data-testid={`offer-feature-${o.id}`} onClick={() => toggleFeatured(o)}
                      className={`p-1.5 rounded-lg ${o.featured ? "text-terracotta" : "text-ink-soft/40 hover:text-ink-soft"}`} title="Feature on homepage">
                      <Star size={18} fill={o.featured ? "currentColor" : "none"} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Editor dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="offer-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto bg-sand">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">{editingId ? "Edit Offer" : "New Offer"}</DialogTitle>
            <DialogDescription className="text-ink-soft text-sm">Fill in the trip details, then preview and save.</DialogDescription>
          </DialogHeader>

          {preview ? (
            <div className="bg-white rounded-2xl border border-bone overflow-hidden">
              {form.image_url && <img src={fileUrl(form.image_url)} alt="" className="w-full h-48 object-cover" />}
              <div className="p-5">
                <p className="text-xs text-terracotta font-semibold uppercase">{form.travel_type}</p>
                <h3 className="font-serif text-2xl text-ink mt-1">{form.title || "Untitled offer"}</h3>
                {fmtDates(form) && <p className="text-sm text-ink-soft mt-1">{fmtDates(form)}</p>}
                {destInput && <p className="text-sm text-ink-soft">{destInput}</p>}
                <p className="text-ink-soft text-sm mt-2">{form.short_description}</p>
                {form.starting_price && <p className="mt-2 text-ink font-semibold">From {form.starting_price}</p>}
                <p className="text-sm text-ink-soft mt-3 whitespace-pre-line">{form.full_description}</p>
                <Button className="mt-4 rounded-full bg-terracotta text-white">{form.cta_label || "Request Information"}</Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Offer Title" className="sm:col-span-2">
                <Input data-testid="offer-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="rounded-xl border-bone bg-white" />
              </Field>
              <Field label="Trip Category">
                <Select value={form.travel_type} onValueChange={(v) => set("travel_type", v)}>
                  <SelectTrigger data-testid="offer-type" className="rounded-xl border-bone bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="offer-status" className="rounded-xl border-bone bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Destinations (comma separated)" className="sm:col-span-2">
                <Input data-testid="offer-destinations" value={destInput} onChange={(e) => setDestInput(e.target.value)} placeholder="Rome, Santorini, Barcelona" className="rounded-xl border-bone bg-white" />
              </Field>
              <Field label="Start Date">
                <Input data-testid="offer-start" type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className="rounded-xl border-bone bg-white" />
              </Field>
              <Field label="End Date">
                <Input data-testid="offer-end" type="date" value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)} className="rounded-xl border-bone bg-white" />
              </Field>
              <Field label="Booking Deadline (optional)">
                <Input type="date" value={form.booking_deadline || ""} onChange={(e) => set("booking_deadline", e.target.value)} className="rounded-xl border-bone bg-white" />
              </Field>
              <Field label="Auto-Publish On (optional)">
                <Input data-testid="offer-publish-at" type="datetime-local" value={form.publish_at || ""} onChange={(e) => set("publish_at", e.target.value)} className="rounded-xl border-bone bg-white" />
              </Field>
              <Field label="Auto-Expire On (optional)">
                <Input data-testid="offer-expire-at" type="datetime-local" value={form.expire_at || ""} onChange={(e) => set("expire_at", e.target.value)} className="rounded-xl border-bone bg-white" />
              </Field>
              <Field label="Starting Price">
                <Input value={form.starting_price} onChange={(e) => set("starting_price", e.target.value)} placeholder="$1,299" className="rounded-xl border-bone bg-white" />
              </Field>
              <Field label="Short Description" className="sm:col-span-2">
                <Input value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className="rounded-xl border-bone bg-white" />
              </Field>
              <Field label="Full Description" className="sm:col-span-2">
                <Textarea value={form.full_description} onChange={(e) => set("full_description", e.target.value)} className="rounded-xl border-bone bg-white min-h-[90px]" />
              </Field>
              <Field label="Deposit Amount">
                <Input value={form.deposit_amount} onChange={(e) => set("deposit_amount", e.target.value)} className="rounded-xl border-bone bg-white" />
              </Field>
              <Field label="Cancellation Notes">
                <Input value={form.cancellation_notes} onChange={(e) => set("cancellation_notes", e.target.value)} className="rounded-xl border-bone bg-white" />
              </Field>

              <Field label="Hero Image" className="sm:col-span-2">
                <div className="flex items-center gap-3">
                  {form.image_url && <img src={fileUrl(form.image_url)} alt="" className="h-16 w-24 object-cover rounded-lg border border-bone" />}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="offer-image-input" />
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full border-ink/20 gap-2">
                    {uploading ? <Loader2 className="animate-spin" size={16} /> : <ImagePlus size={16} />} Upload
                  </Button>
                </div>
                {!form.image_url && PUBLIC_STATUSES.includes(form.status) && (
                  <p data-testid="editor-image-nudge" className="flex items-center gap-1.5 text-xs text-amber-700 mt-1.5">
                    <AlertTriangle size={13} /> This trip is live — add a hero image so its page doesn't launch with a plain background.
                  </p>
                )}
              </Field>

              <Field label="Featured on homepage">
                <div className="flex items-center h-10"><Switch checked={form.featured} onCheckedChange={(c) => set("featured", c)} data-testid="offer-featured" /></div>
              </Field>
              <Field label="CTA Button">
                <Select value={form.cta_label} onValueChange={(v) => set("cta_label", v)}>
                  <SelectTrigger data-testid="offer-cta-label" className="rounded-xl border-bone bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{CTA_LABELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="CTA Action">
                <Select value={form.cta_action} onValueChange={(v) => set("cta_action", v)}>
                  <SelectTrigger className="rounded-xl border-bone bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{CTA_ACTIONS.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              {form.cta_action === "external_link" && (
                <Field label="CTA URL">
                  <Input value={form.cta_url} onChange={(e) => set("cta_url", e.target.value)} placeholder="https://" className="rounded-xl border-bone bg-white" />
                </Field>
              )}

              {/* ---- Dedicated trip page content ---- */}
              <div className="sm:col-span-2 pt-4 mt-2 border-t border-bone">
                <h3 className="font-serif text-xl font-semibold text-ink">Dedicated Trip Page</h3>
                <p className="text-xs text-ink-soft mt-0.5">Optional. Fill only what you need — empty sections stay hidden. Shared notes (flights, insurance, terms, Meet Juliet) come from Homepage Content.</p>
              </div>

              <Field label="Pricing note (optional)" className="sm:col-span-2">
                <Input data-testid="offer-pricing-note" value={form.pricing_note || ""} onChange={(e) => set("pricing_note", e.target.value)} placeholder="per person, double occupancy" className="rounded-xl border-bone bg-white" />
              </Field>

              <Field label="What's included — one per line">
                <Textarea data-testid="offer-included" value={listToLines(form.whats_included)} onChange={(e) => set("whats_included", linesToList(e.target.value))} className="rounded-xl border-bone bg-white min-h-[120px]" placeholder={"7 nights accommodation\nDaily breakfast\nAirport transfers"} />
              </Field>
              <Field label="What's NOT included — one per line">
                <Textarea data-testid="offer-not-included" value={listToLines(form.whats_not_included)} onChange={(e) => set("whats_not_included", linesToList(e.target.value))} className="rounded-xl border-bone bg-white min-h-[120px]" placeholder={"International flights\nTravel insurance\nGratuities"} />
              </Field>

              <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-xl bg-white/60 border border-bone p-4">
                <Field label="Accommodation details" className="sm:col-span-2">
                  <Textarea data-testid="offer-accommodation" value={form.accommodation || ""} onChange={(e) => set("accommodation", e.target.value)} className="rounded-xl border-bone bg-white min-h-[90px]" placeholder="Describe the resort, room type, views…" />
                </Field>
                <SectionToggle label="Show Accommodation section" testid="toggle-accommodation" checked={form.show_accommodation} onChange={(c) => set("show_accommodation", c)} />
              </div>

              {/* Itinerary repeater */}
              <div className="sm:col-span-2 rounded-xl bg-white/60 border border-bone p-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-ink font-medium">Itinerary (day by day)</Label>
                  <SectionToggle label="Show" testid="toggle-itinerary" checked={form.show_itinerary} onChange={(c) => set("show_itinerary", c)} inline />
                </div>
                <div className="space-y-3">
                  {(form.itinerary || []).map((s, i) => (
                    <div key={i} data-testid={`itinerary-row-${i}`} className="flex gap-2 items-start">
                      <div className="flex-1 space-y-2">
                        <Input value={s.title} onChange={(e) => updateRow("itinerary", i, { title: e.target.value })} placeholder={`Day ${i + 1} title`} className="rounded-lg border-bone bg-white" />
                        <Textarea value={s.detail} onChange={(e) => updateRow("itinerary", i, { detail: e.target.value })} placeholder="What happens this day" className="rounded-lg border-bone bg-white min-h-[60px]" />
                      </div>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeRow("itinerary", i)} className="text-red-500 shrink-0"><Trash2 size={16} /></Button>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" onClick={() => addRow("itinerary", { title: "", detail: "" })} data-testid="add-itinerary" className="mt-3 rounded-full border-ink/20 gap-2"><Plus size={14} /> Add day</Button>
              </div>

              {/* Gallery uploader */}
              <div className="sm:col-span-2 rounded-xl bg-white/60 border border-bone p-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-ink font-medium">Gallery images</Label>
                  <SectionToggle label="Show" testid="toggle-gallery" checked={form.show_gallery} onChange={(c) => set("show_gallery", c)} inline />
                </div>
                <div className="flex flex-wrap gap-3">
                  {(form.gallery || []).map((g, i) => (
                    <div key={i} data-testid={`gallery-thumb-${i}`} className="relative h-20 w-28 rounded-lg overflow-hidden border border-bone">
                      <img src={fileUrl(g)} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeGallery(g)} className="absolute top-1 right-1 bg-ink/70 text-white rounded-full p-0.5"><X size={12} /></button>
                    </div>
                  ))}
                </div>
                <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={uploadGallery} data-testid="offer-gallery-input" />
                <Button type="button" variant="outline" onClick={() => galleryRef.current?.click()} disabled={uploadingGallery} className="mt-3 rounded-full border-ink/20 gap-2">
                  {uploadingGallery ? <Loader2 className="animate-spin" size={16} /> : <ImagePlus size={16} />} Add images
                </Button>
              </div>

              {/* Offer FAQs repeater */}
              <div className="sm:col-span-2 rounded-xl bg-white/60 border border-bone p-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-ink font-medium">Trip-specific FAQs</Label>
                  <SectionToggle label="Show" testid="toggle-faqs" checked={form.show_faqs} onChange={(c) => set("show_faqs", c)} inline />
                </div>
                <div className="space-y-3">
                  {(form.offer_faqs || []).map((f, i) => (
                    <div key={i} data-testid={`offer-faq-row-${i}`} className="flex gap-2 items-start">
                      <div className="flex-1 space-y-2">
                        <Input value={f.question} onChange={(e) => updateRow("offer_faqs", i, { question: e.target.value })} placeholder="Question" className="rounded-lg border-bone bg-white" />
                        <Textarea value={f.answer} onChange={(e) => updateRow("offer_faqs", i, { answer: e.target.value })} placeholder="Answer" className="rounded-lg border-bone bg-white min-h-[60px]" />
                      </div>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeRow("offer_faqs", i)} className="text-red-500 shrink-0"><Trash2 size={16} /></Button>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" onClick={() => addRow("offer_faqs", { question: "", answer: "" })} data-testid="add-offer-faq" className="mt-3 rounded-full border-ink/20 gap-2"><Plus size={14} /> Add FAQ</Button>
              </div>

              <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SectionToggle label="Show 'Meet Juliet' section" testid="toggle-meet-juliet" checked={form.show_meet_juliet} onChange={(c) => set("show_meet_juliet", c)} boxed />
                <SectionToggle label="Show 'Related Trips' section" testid="toggle-related" checked={form.show_related} onChange={(c) => set("show_related", c)} boxed />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setPreview((p) => !p)} className="rounded-full border-ink/20 gap-2">
              <Eye size={16} /> {preview ? "Edit" : "Preview"}
            </Button>
            <Button data-testid="offer-save" onClick={save} disabled={saving} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white">
              {saving ? <Loader2 className="animate-spin" size={16} /> : (editingId ? "Save Offer" : "Create Offer")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, className = "", children }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-ink font-medium text-sm">{label}</Label>
    {children}
  </div>
);

const SectionToggle = ({ label, testid, checked, onChange, inline = false, boxed = false }) => {
  if (inline) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-ink-soft">{label}</span>
        <Switch data-testid={testid} checked={!!checked} onCheckedChange={onChange} />
      </div>
    );
  }
  return (
    <div className={`flex items-center justify-between ${boxed ? "rounded-xl bg-white/60 border border-bone p-4" : ""}`}>
      <Label className="text-ink font-medium text-sm">{label}</Label>
      <Switch data-testid={testid} checked={!!checked} onCheckedChange={onChange} />
    </div>
  );
};
